import React, { useRef, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import './IsolinesMaterial';
import { MathType, ThreeElements, useFrame } from '@react-three/fiber';
import { isMesh } from '@slope/three';
import { IsolinesMaterialType } from './IsolinesMaterial';

export type IsolinesProps = ThreeElements['mesh'] & {
  mesh?: React.RefObject<THREE.Mesh>;
  color?: MathType<THREE.Color>;
  radius?: number;
  lineThickness?: number;
  lineDensity?: number;
  opacity?: number;
  depthWrite?: boolean;
  animationSpeed?: number;
  polygonOffsetFactor?: number;
  debug?: boolean;
};

export const Isolines: React.FC<IsolinesProps> = ({
  mesh,
  radius = 5,
  color = '#ffffff',
  depthWrite,
  opacity = 1,
  lineThickness = 1,
  lineDensity = 10,
  polygonOffsetFactor = -1,
  animationSpeed = 0,
  debug = false,
  children,
  ...restProps
}) => {
  const meshRef = useRef<THREE.Mesh>(null!);
  const mateRef = useRef<IsolinesMaterialType>(null!);

  const [parent, setParent] = useState<THREE.Mesh | undefined>(undefined);

  const bbox = useMemo(() => {
    if (!parent) {
      return new THREE.Box3();
    }

    parent.updateWorldMatrix(true, false);
    return new THREE.Box3().setFromObject(parent, true);
  }, [parent]);

  const shaderProps = useMemo(() => {
    if (!parent) {
      return {};
    }

    return {
      boxMin: bbox.min,
      boxMax: bbox.max,
    };
  }, [bbox]);

  useEffect(() => {
    const parent = mesh?.current ?? meshRef.current?.parent;
    if (!isMesh(parent)) {
      throw new Error('Isolines must have a Mesh as parent or specify its "mesh" prop');
    }
    setParent(parent);
  }, [mesh, mesh?.current ?? meshRef.current?.parent]);

  useFrame((_, deltaTime) => {
    if (mateRef.current && animationSpeed) {
      mateRef.current.uniforms.time.value += deltaTime * animationSpeed;
    }
  });

  return (
    <mesh ref={meshRef} {...restProps} geometry={parent?.geometry}>
      <isolinesMaterial
        ref={mateRef}
        transparent
        depthTest={false}
        {...shaderProps}
        polygonOffset
        polygonOffsetFactor={polygonOffsetFactor}
        opacity={opacity}
        depthWrite={depthWrite}
        lineThickness={lineThickness}
        lineDensity={lineDensity}
      />
      {children}
      {debug && <box3Helper args={[bbox, 'royalblue']} />}
    </mesh>
  );
};
